<script setup>
import { ref, computed, watch } from "vue"
import { usePricingPlans } from "@/composables/usePricingPlans.js"
import { useEmailValidation } from "@/composables/useEmailValidation.js"
import { useDomainValidation } from "@/composables/useDomainValidation.js"
import { useRegions } from "@/composables/useRegions.js"
import { useSubscriptions } from "@/composables/useSubscriptions.js"
import { useCreateSubscriptionActions } from "@/composables/useCreateSubscriptionActions.js"
import { useHierarchy } from "@/composables/useHierarchy.js"
import { useAccount } from "@/composables/useAccount.js"
import { useDisplay } from "vuetify"

const customerForm = ref(null)
const resellerForm = ref(null)
const dialog = ref(false)
const region = ref()
const currentStep = ref(1)
const lastStep = ref(4)
const subdomain = ref("")
const email = ref("")
const company = ref("")
const reseller = ref(null)
const isNgoEduGov = ref(false)
const selectedInstanceOnDev = ref(import.meta.env.VITE_CHARGEBEE_SITE === "paessler")
const steps = ref([
  {
    title: "Customer details",
    name: "Enter the end customer details.",
    message: "Generic E-Mail account recommended.",
    isValid: true,
    isCompleted: false,
    isOptional: false,
    id: 1,
    form: customerForm,
  },
  {
    title: "Subdomain",
    name: "Do you want to specify a subdomain where the PRTG server will be available?",
    message: "If you leave this field empty, we will assign a random one for you (can be changed later).",
    isValid: true,
    isCompleted: false,
    isOptional: true,
    id: 2,
  },
  {
    title: "Region",
    name: "Select the hosting region.",
    isValid: true,
    isCompleted: false,
    isOptional: true,
    id: 3,
  },
  {
    title: "Summary",
    name: "Check your provided information before creating a subscription.",
    isValid: true,
    isCompleted: false,
    isOptional: false,
    id: 4,
  },
])
const { regionsByNation, nearestRegion } = useRegions()
const { trialPlan } = usePricingPlans()
const {
  validateDomain,
  error: errorDomain,
  errorMessage: errorMessageDomain,
  isLoading: isLoadingDomain,
} = useDomainValidation(subdomain)
const {
  validateEmail,
  error: errorEmail,
  errorMessage: errorMessageEmail,
  isLoading: isLoadingEmail,
  requires,
  userId,
  organizationId,
} = useEmailValidation(email, reseller)
const {
  proceedToCreate,
  isLoading: isLoadingCreateSubscription,
} = useCreateSubscriptionActions(email, requires, userId, organizationId, company, subdomain, selectedInstanceOnDev, region, trialPlan, isNgoEduGov, reseller)
const { refetchSubscriptions } = useSubscriptions()
const { user } = useAccount()
const isDistributor = computed(() => user.value?.role === "distributor")
const { subordinates } = useHierarchy(isDistributor)

watch(
  isDistributor,
  (val) => {
    if (!val) {
      return
    }

    // Avoid adding the reseller step multiple times
    if (steps.value.some(step => step.id === 0)) {
      return
    }

    steps.value.unshift({
      title: "Reseller",
      name: "Select the involved reseller.",
      isValid: true,
      isCompleted: false,
      isOptional: false,
      id: 0,
      items: subordinates,
      form: resellerForm,
    })
    lastStep.value += 1
  },
  { immediate: true },
)

const domainName = import.meta.env.VITE_PRTG_DOMAIN_NAME
const fullDomainName = computed(() =>
  subdomain.value
    ? `${subdomain.value}.${domainName}`
    : "- random generated -")

watch(
  nearestRegion,
  () => {
    region.value = nearestRegion.value
  }, { immediate: true },
)
const emit = defineEmits(["close"])
watch(dialog, (val) => {
  if (val === false) {
    emit("close")
  }
})

const createSubscription = async () => {
  await proceedToCreate()
  dialog.value = false
  return refetchSubscriptions()
}
const validateAndStep = async () => {
  if (isLoadingCreateSubscription.value) {
    return
  }

  const step = steps.value[currentStep.value - 1]
  if (step.id === 0) {
    const { valid } = await resellerForm.value[0].validate()
    step.isValid = valid
  }
  else if (step.id === 1) {
    const { valid } = await customerForm.value[0].validate()
    await validateEmail(email.value)
    step.isValid = valid && !errorEmail.value
  }
  else if (step.id === 2) {
    await validateDomain(subdomain.value)
    step.isValid = !errorDomain.value
  }

  step.isCompleted = step.isValid

  if (step.isValid) {
    currentStep.value += 1
  }
}

const { mdAndUp } = useDisplay()
const isDev = ref(import.meta.env.VITE_CHARGEBEE_SITE === "paessler-test")
const summaryTextFields = computed(() => {
  const textFields = [
    {
      label: "Customer Email",
      value: email.value,
    },
    {
      label: "Customer Company",
      value: company.value,
    },
    {
      label: "Domain",
      value: fullDomainName,
    },
    {
      label: "Region",
      value: region.value.name,
    },
  ]

  const extraTextField = {
    label: "Involved Reseller",
    value: reseller.value?.email,
    isDistributor,
  }
  return isDistributor.value ? [extraTextField, ...textFields] : textFields
})
</script>

<template>
  <v-dialog
    v-model="dialog"
    max-width="850"
    persistent
  >
    <v-stepper v-model="currentStep">
      <v-stepper-header>
        <template
          v-for="(step, n) in steps"
          :key="`${n}-step`"
        >
          <v-stepper-item
            :value="n + 1"
            :complete="step.isCompleted"
            :error="!step.isValid"
            :title="step.title"
            :subtitle="step.isOptional ? 'Optional' : ''"
          />

          <v-divider
            v-if="n < steps.length - 1"
            :key="n"
          />
        </template>
        <v-progress-linear
          key="progress"
          absolute
          location="bottom"
          :size="50"
          :active="isLoadingCreateSubscription"
          :indeterminate="isLoadingCreateSubscription"
        />
      </v-stepper-header>

      <v-stepper-window>
        <v-stepper-window-item
          v-for="(step, n) in steps"
          :key="`${n}-content`"
          :data-test-step-content="n + 1"
          :value="n + 1"
        >
          <v-card max-height="400">
            <v-card-item>
              <v-card-title>
                {{ step.name }}
              </v-card-title>
              <v-card-subtitle class="text-body-large">
                {{ step.message }}
              </v-card-subtitle>
            </v-card-item>

            <v-card-text v-if="isDistributor && step.id === 0">
              <v-form ref="resellerForm">
                <v-autocomplete
                  v-model="reseller"
                  label="Involved reseller"
                  return-object
                  :items="step.items"
                  :item-title="item => `${item.company ? item.company : 'n/a'} (${item.email})`"
                  :rules="[v => Object.keys(v || {}).length > 0 || 'Reseller required.']"
                />
              </v-form>
            </v-card-text>

            <v-card-text v-else-if="step.id === 1">
              <v-form ref="customerForm">
                <v-text-field
                  v-model="email"
                  label="E-Mail"
                  :loading="isLoadingEmail"
                  :error="errorEmail"
                  :error-messages="errorMessageEmail"
                  :rules="[v => v.length > 0 || 'Email address required.']"
                />
                <v-text-field
                  v-model="company"
                  label="Company"
                  :rules="[v => v.length > 0 || 'Company required.']"
                />
                <v-checkbox
                  v-model="isNgoEduGov"
                  label="Customer qualifies for EDU/GOV/NGO/NPO-discount."
                  hide-details
                />
              </v-form>
            </v-card-text>

            <v-card-text v-else-if="step.id === 2">
              <v-text-field
                v-model="subdomain"
                label="Subdomain (optional)"
                placeholder="e. g. placeholder-domain"
                :loading="isLoadingDomain"
                :error="errorDomain"
                :error-messages="errorMessageDomain"
                :suffix="`.${domainName}`"
              />
              <v-checkbox
                v-if="isDev"
                v-model="selectedInstanceOnDev"
                label="Create a real PPHM Instance. Only check this box if you need a subscription AND access to its PRTG.
                Certain (Support) Actions like File Upload, Enterprise Connection or RDP Access, will only work with a real PPHM Instance."
              />
            </v-card-text>

            <v-card-text v-else-if="step.id === 3">
              <v-select
                v-model="region"
                :items="regionsByNation"
                label="Region (optional)"
                item-title="location"
                item-value="code"
                return-object
              >
                <template #item="{ props }">
                  <v-list-subheader v-if="props?.header">
                    {{ props?.header }}
                  </v-list-subheader>
                  <v-divider v-else-if="props?.divider" />
                  <v-list-item
                    v-else
                    v-bind="props"
                  />
                </template>
              </v-select>
            </v-card-text>
            <v-card-text v-else-if="step.id === 4">
              <v-text-field
                v-for="{ label, value } in summaryTextFields"
                :key="label"
                :label="label"
                :model-value="value"
                readonly
              />
            </v-card-text>
          </v-card>
        </v-stepper-window-item>
      </v-stepper-window>

      <v-divider />

      <v-stepper-actions
        :disabled="false"
        class="px-4 py-2"
      >
        <template #next>
          <div>
            <v-btn
              v-if="currentStep > 1"
              class="mr-2"
              color="secondary"
              data-test-prev-btn
              @click="currentStep -= 1"
            >
              Previous
            </v-btn>
            <v-btn
              v-if="currentStep < lastStep"
              color="primary"
              :loading="isLoadingEmail || isLoadingDomain"
              data-test-next-btn
              @click="validateAndStep()"
            >
              Next
            </v-btn>
            <v-btn
              v-else
              color="primary"
              data-test-create-btn
              :loading="isLoadingCreateSubscription"
              @click="createSubscription"
            >
              Create Free Trial Subscription
            </v-btn>
          </div>
        </template>
        <template #prev>
          <v-btn
            data-test-cancel-btn
            @click="dialog = false"
          >
            cancel
          </v-btn>
        </template>
      </v-stepper-actions>
    </v-stepper>
    <template #activator="{ props: _props }">
      <v-btn
        size="small"
        data-test-open-dialog-btn
        v-bind="_props"
      >
        <v-icon
          color="white"
          :start="mdAndUp"
        >
          $mdiPlus
        </v-icon>
        <template v-if="mdAndUp">
          Create new subscription
        </template>
      </v-btn>
    </template>
  </v-dialog>
</template>
